import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import AudioPlayer from "../coursepages/moduledetailpage/AudioPlayer";
import "./AudioGradingModal.css";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import RubricEditor from "./RubricEditor";

const CACHE_KEY_PREFIX = "activeRubric_";
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

const getCachedRubric = (courseId) => {
  const key = `${CACHE_KEY_PREFIX}${courseId}`;
  const cached = localStorage.getItem(key);

  if (cached) {
    const { data, timestamp, version } = JSON.parse(cached);
    const timeLeft = CACHE_DURATION - (Date.now() - timestamp);
    console.log(`Cache check for ${key}:`, {
      found: true,
      timeLeftMinutes: Math.round(timeLeft / 1000 / 60),
      version,
      data,
    });

    if (timeLeft > 0) {
      return { data, version };
    }
    console.log(`Cache expired for ${key}, clearing...`);
    localStorage.removeItem(key);
  }
  return null;
};

const setCachedRubric = (courseId, rubricData) => {
  const key = `${CACHE_KEY_PREFIX}${courseId}`;
  const cache = {
    data: rubricData,
    timestamp: Date.now(),
    version: 1,
  };
  localStorage.setItem(key, JSON.stringify(cache));
  console.log(`Cache set for ${key}:`, rubricData);
};

const AudioGradingModal = ({
  isOpen,
  onClose,
  onSubmit,
  question,
  currentGrade,
  audioUrl,
  courseId,
}) => {
  const [rubricData, setRubricData] = useState(
    Array(3)
      .fill()
      .map(() => Array(3).fill(""))
  );
  const [columnHeaders, setColumnHeaders] = useState(
    Array(3)
      .fill()
      .map((_, i) => `${i}/2`)
  );
  const [rowHeaders, setRowHeaders] = useState(Array(3).fill("Criteria"));
  const [isLoadingRubric, setIsLoadingRubric] = useState(false);

  const [grade, setGrade] = useState({
    score: currentGrade?.score || 0,
    feedback: currentGrade?.feedback || "",
    selectedCells: Array(3).fill(null),
  });

  const [isEditingRubric, setIsEditingRubric] = useState(false);

  useEffect(() => {
    const initializeRubric = async () => {
      setIsLoadingRubric(true);
      try {
        console.log("Initializing rubric with question data:", question);

        if (question?.rubric) {
          const { content, rows, cols } = question.rubric;
          setRubricData(content);
          setColumnHeaders(cols);
          setRowHeaders(rows);
          return;
        }

        const fetchActiveRubric = httpsCallable(functions, "fetchActiveRubric");
        const activeRubricResponse = await fetchActiveRubric({ courseId });

        if (activeRubricResponse.data.activeRubric) {
          const { content, rows, cols } =
            activeRubricResponse.data.activeRubric;
          setRubricData(content);
          setColumnHeaders(cols);
          setRowHeaders(rows);
        }
      } catch (error) {
        console.error("Error initializing rubric:", error);
      } finally {
        setIsLoadingRubric(false);
      }
    };

    if (question || courseId) {
      initializeRubric();
    }
  }, [question, courseId]);

  console.log("Current state:", { rubricData, columnHeaders, rowHeaders });

  const handleCellClick = (rowIndex, colIndex) => {
    setGrade((prev) => {
      const newSelectedCells = [...prev.selectedCells];
      newSelectedCells[rowIndex] = { row: rowIndex, col: colIndex };

      // Calculate points per row
      const totalRows = rowHeaders.length;
      const pointsPerRow = question.score / totalRows;

      // Calculate points per column increment
      const totalCols = columnHeaders.length;
      const pointsPerIncrement = pointsPerRow / (totalCols - 1);

      // Calculate total score based on selected cells
      const totalScore = newSelectedCells
        .filter((cell) => cell !== null)
        .reduce((sum, cell) => {
          return sum + cell.col * pointsPerIncrement;
        }, 0);

      // Round to 2 decimal places instead of using Math.round
      const finalScore = Number(totalScore.toFixed(2));

      console.log("Score calculation:", {
        pointsPerRow,
        pointsPerIncrement,
        selections: newSelectedCells,
        totalScore,
        finalScore,
      });

      return {
        ...prev,
        score: finalScore,
        selectedCells: newSelectedCells,
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      score: grade.score,
      feedback: grade.feedback,
      selectedCells: grade.selectedCells,
      isAutoCalculated: true,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="rubric-modal-overlay">
      <div className="rubric-modal-content">
        <div className="rubric-modal-header">
          <h2>Grade Audio Recording</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {audioUrl && (
          <div className="audio-player-container">
            <AudioPlayer
              audioURL={audioUrl}
              border="shadow"
              isStudentView={true}
              question=""
              onChange={() => {}}
              onRetry={null}
              tip={null}
            />
          </div>
        )}

        <div>
          <div className="rubric-display">
            <div className="rubric-header">
              <h3>Grading Rubric</h3>
              <button
                className="edit-rubric-button"
                onClick={() => setIsEditingRubric(true)}
              >
                {rubricData.some((row) => row.some((cell) => cell))
                  ? "Edit Rubric"
                  : "Create Rubric"}
              </button>
            </div>
            {isLoadingRubric ? (
              <div className="loading-message">Loading rubric...</div>
            ) : rubricData.some((row) => row.some((cell) => cell)) ? (
              <>
                <div className="rubric-grid">
                  <div className="rubric-row header-row">
                    <div className="empty-corner-cell"></div>
                    {columnHeaders.map((header, index) => (
                      <div key={index} className="header-cell">
                        {header}
                      </div>
                    ))}
                  </div>
                  {rubricData.map((row, rowIndex) => (
                    <div key={rowIndex} className="rubric-row">
                      <div className="row-header-cell">
                        {rowHeaders[rowIndex]}
                      </div>
                      {row.map((cell, colIndex) => (
                        <div
                          key={colIndex}
                          className="rubric-cell clickable"
                          onClick={() => handleCellClick(rowIndex, colIndex)}
                          style={{
                            backgroundColor:
                              grade.selectedCells[rowIndex]?.row === rowIndex &&
                              grade.selectedCells[rowIndex]?.col === colIndex
                                ? "#e3f2fd"
                                : "white",
                          }}
                        >
                          {cell}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="loading-message">Loading rubric...</div>
            )}
          </div>
        </div>

        <div className="score-display">
          Score:
          <input
            type="number"
            value={grade.score}
            onChange={(e) => {
              const newScore = Math.min(
                Math.max(0, Number(e.target.value)),
                question.score
              );
              setGrade((prev) => ({
                ...prev,
                score: Number(newScore.toFixed(2)),
              }));
            }}
            step="0.1"
            min="0"
            max={question.score}
          />
          /{question.score}
        </div>

        {isEditingRubric && (
          <RubricEditor
            show={isEditingRubric}
            onClose={() => setIsEditingRubric(false)}
            onSave={(rubricData) => {
              setRubricData(rubricData.data);
              setRowHeaders(rubricData.rowHeaders);
              setColumnHeaders(rubricData.columnHeaders);
              setIsEditingRubric(false);
            }}
            initialRubric={{
              data: rubricData,
              rowHeaders: rowHeaders,
              columnHeaders: columnHeaders,
            }}
            maxScore={question.score || 0}
            courseId={courseId}
          />
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <textarea
              value={grade.feedback}
              onChange={(e) => setGrade({ ...grade, feedback: e.target.value })}
              placeholder="Add feedback..."
              rows={4}
            />
          </div>
          <div className="modal-footer">
            <button type="submit" className="submit-button">
              Submit
            </button>
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AudioGradingModal;
